import type {
	LegacyAssistantMessageDTO,
	MessageConfirmationStatus,
	MessageContentItem,
	MessageRole,
	MessageToolCall,
	PublicMessageDTO,
} from "shared/types/MessageContracts";

export type ConfirmationStatusUI = "pending" | "approved" | "denied";

export interface ExtractedMessageConfirmation {
	confirmationId: string;
	toolCallId: string;
	toolName: string;
	description: string;
	status: ConfirmationStatusUI;
	reason?: string;
	toolCall: MessageToolCall;
}

export function isPublicMessageDTO(message: unknown): message is PublicMessageDTO {
	if (!message || typeof message !== "object") return false;
	return "role" in message;
}

export function isLegacyAssistantMessageDTO(message: unknown): message is LegacyAssistantMessageDTO {
	if (!message || typeof message !== "object") return false;
	return "senderType" in message;
}

function extractTextFromContentItem(item: any): string {
	if (!item || typeof item !== "object") return "";

	if (item.type === "text") {
		if (typeof item.content === "string") return item.content;
		if (typeof item.text === "string") return item.text;
	}

	if (item.type === "audio" && typeof item.audio?.transcript === "string") {
		return item.audio.transcript;
	}

	if (item.type === "file") {
		const fileParts = item.file?.content;
		if (Array.isArray(fileParts)) {
			const fileTexts = fileParts
				.map((filePart: any) => {
					if (filePart?.type === "text" && typeof filePart.text === "string") return filePart.text;
					if (filePart?.type === "text" && typeof filePart.content === "string") return filePart.content;
					return "";
				})
				.filter(Boolean);
			return fileTexts.join("\n\n");
		}
	}

	if (typeof item.content === "string") return item.content;
	if (typeof item.text === "string") return item.text;
	if (typeof item.result === "string") return item.result;

	return "";
}

export function extractTextFromMessageContent(content: unknown): string {
	if (typeof content === "string") return content.trim();

	if (content && typeof content === "object" && !Array.isArray(content)) {
		const fromObject = extractTextFromContentItem(content);
		if (fromObject) return fromObject.trim();
	}

	if (!Array.isArray(content)) return "";

	const parts: string[] = [];

	for (const item of content) {
		const extracted = extractTextFromContentItem(item);
		if (extracted) parts.push(extracted);
	}

	return parts.join("\n\n").trim();
}

export function extractTextFromMessage(message: PublicMessageDTO | LegacyAssistantMessageDTO): string {
	if (isLegacyAssistantMessageDTO(message)) {
		if (typeof message.content === "string") return message.content.trim();
		return extractTextFromMessageContent(message.content);
	}

	return extractTextFromMessageContent(message.content);
}

export function extractToolCallsFromContent(content: MessageContentItem[] | null | undefined): MessageToolCall[] {
	if (!Array.isArray(content)) return [];

	return content
		.filter((item): item is { type: "tool_calls"; toolCalls: MessageToolCall[] } => item?.type === "tool_calls")
		.flatMap((item) => (Array.isArray(item.toolCalls) ? item.toolCalls : []));
}

export function extractConfirmationsFromMessage(message: PublicMessageDTO | LegacyAssistantMessageDTO): ExtractedMessageConfirmation[] {
	if (isLegacyAssistantMessageDTO(message)) {
		const metadata = message.metadata ?? {};
		const confirmationId = metadata.confirmationId as string | undefined;
		if (!confirmationId) return [];

		return [
			{
				confirmationId,
				toolCallId: (metadata.toolCallId as string | undefined) ?? confirmationId,
				toolName: (metadata.toolCall?.name as string | undefined) ?? "tool",
				description: (message.content || metadata.description || "Deseja executar esta ferramenta?") as string,
				status: toConfirmationStatusUI(metadata.status as string | undefined),
				reason: metadata.reason as string | undefined,
				toolCall: {
					id: (metadata.toolCall?.id as string | undefined) ?? confirmationId,
					type: "function",
					function: {
						name: (metadata.toolCall?.name as string | undefined) ?? "tool",
						arguments: JSON.stringify(metadata.toolCall?.arguments ?? {}),
					},
					description: metadata.description as string | undefined,
				},
			},
		];
	}

	const toolCalls = extractToolCallsFromContent(message.content);

	return toolCalls
		.map((toolCall): ExtractedMessageConfirmation | null => {
			const confirmation = toolCall.confirmation;
			if (!confirmation) return null;

			const confirmationId = confirmation.id ?? confirmation.confirmationId;
			if (!confirmationId) return null;

			return {
				confirmationId,
				toolCallId: toolCall.id,
				toolName: toolCall.function.name,
				description: confirmation.prompt || toolCall.description || `Deseja executar ${toolCall.function.name}?`,
				status: toConfirmationStatusUI(confirmation.status, confirmation.approved),
				reason: confirmation.reason,
				toolCall,
			};
		})
		.filter((item): item is ExtractedMessageConfirmation => item !== null);
}

export function toConfirmationStatusUI(status?: string, approved?: boolean): ConfirmationStatusUI {
	if (status === "pending") return "pending";
	if (status === "approved" || status === "confirmed") return "approved";
	if (status === "denied" || status === "rejected") return "denied";

	if (approved === true) return "approved";
	if (approved === false) return "denied";

	return "pending";
}

export function roleToSenderType(role: MessageRole): "user" | "assistente" | null {
	if (role === "user") return "user";
	if (role === "assistant") return "assistente";
	return null;
}

export function normalizeMessageMetadata(metadata: Record<string, any> | undefined): Record<string, any> {
	return metadata ?? {};
}

export function firstMessageFromPossibleArray<T>(value: T | T[] | undefined): T | undefined {
	if (!value) return undefined;
	return Array.isArray(value) ? value[0] : value;
}

export function isPendingConfirmationStatus(status?: MessageConfirmationStatus | string): boolean {
	return toConfirmationStatusUI(status) === "pending";
}
